import ApiClient from './ApiClient';
import PortOpeningRequestOnPrem from '../models/entities/PortOpeningRequestOnPrem';
import SubmitPortOpeningRequestOnPrem from '../models/viewModels/OnPremPortOpeningRequests/SubmitPortOpeningRequestOnPrem';
import ApprovePortOpeningRequestOnPrem from '../models/viewModels/OnPremPortOpeningRequests/ApprovePortOpeningRequestOnPrem';
import CompletePortOpeningRequestOnPrem from '../models/viewModels/OnPremPortOpeningRequests/CompletePortOpeningRequestOnPrem';
import UpdatePortOpeningRequestOnPrem from '../models/viewModels/OnPremPortOpeningRequests/UpdatePortOpeningOnPremRequest';
import ReviewReason from '../models/interfaces/ReviewReason'
import PortOpeningRequestApproverVerification from '../models/viewModels/PortOpeningRequests/PortOpeningRequestApproverVerification';
import NetworkConnection from '../models/entities/NetworkConnection';
import RejectionModel from '../models/interfaces/RejectionModel';
import CancellationReason from '../models/interfaces/CancellationReason';
import { OnPremPortOpeningRequestsTypes } from '../models/enums/POR/OnPremPortOpeningRequestsTypes';
import EscalateType from '../models/interfaces/EscalateType';
import PortOpeningRuleOnPrem from '../models/entities/PortOpeningRuleOnPrem';
import OnPremRiskPorts from '../models/entities/OnPremRiskPorts';

export default class OnPremPortOpeningRequestService extends ApiClient {
	async getMyRequests() {
		return this.get<PortOpeningRequestOnPrem[]>('OnPremPortOpeningRequests/me');
	}

    async getImplementedRulesByTrackerIdAndDBMId(scope: string, direction: string, trackerId: number, appId: number) {
        const params = new URLSearchParams();
        
        if (direction) {
            params.append('direction', direction);
        }
        
        if (trackerId && trackerId > 0) {
            params.append('trackerId', trackerId.toString());
        }
        
        if (appId && appId > 0) {
            params.append('appId', appId.toString());
        }
        
        const queryString = params.toString();
        const url = queryString 
            ? `OnPremPortOpeningRequests/implementedRules?${queryString}`
            : 'OnPremPortOpeningRequests/implementedRules';
        
        return this.get<PortOpeningRuleOnPrem[]>(url);
    }

	async getAll() {
		return this.get<PortOpeningRequestOnPrem[]>('OnPremPortOpeningRequests');
	}

	async getImplementedRules() {
		return this.get<PortOpeningRuleOnPrem[]>('OnPremPortOpeningRequests/ImplementedRules');
	}

	async getMyApprovals() {
		return this.get<PortOpeningRequestOnPrem[]>('OnPremPortOpeningRequests/myApprovals');
	}

	async verifyApproverLevel(email: string, isEmergency: boolean) {
		return this.get<PortOpeningRequestApproverVerification>(`PortOpeningRequests/verifyApproverLevel?email=${email}&emergency=${isEmergency}`);
	}

	async create(por: SubmitPortOpeningRequestOnPrem) {
		return this.post<PortOpeningRequestOnPrem>('OnPremPortOpeningRequests', por);
	}

	async update(id: number, por: UpdatePortOpeningRequestOnPrem) {
		return this.patch<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}`, por);
	}

	async getById(id: number) {
		return this.get<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}`);
	}

	async getNCDBbyTrackerId(trackerId: number) {
		return this.get<NetworkConnection>(`OnPremPortOpeningRequests/ncdb/${trackerId}`);
	}

	async getRisksPorts() {
		return this.get<OnPremRiskPorts>(`OnPremPortOpeningRequests/RiskPorts`);
	}

	async doReviewal( review: ReviewReason, id: number) {
		return this.patch<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}/review`, review);
	}

	async sendToDraft( review: ReviewReason, id: number) {
		return this.patch<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}/draft`, review);
	}

	async cancel(portOpeningRequestId: number, cancellationModel: CancellationReason) {
		return this.patch<PortOpeningRequestOnPrem>(
			`OnPremPortOpeningRequests/${portOpeningRequestId}/cancellation`,
			cancellationModel
		);
	}

	async approve(id: number) {
		return this.patch<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}/approval`, null);
	}

	async reject(portOpeningRequestId: number, rejectionModel: RejectionModel) {
		return this.patch<null>(`OnPremPortOpeningRequests/${portOpeningRequestId}/rejection`, rejectionModel);
	}

	async doComplete( complete: CompletePortOpeningRequestOnPrem, id: number) {
		return this.put<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}/complete`, complete);
	}

	async doEscalation( escalate: EscalateType, id: number) {
		return this.patch<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}/escalate`, escalate);
	}

	async doRejection(rejection: RejectionModel, id: number) {
        return this.patch<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}/rejection`, rejection);
    }

	async doApproval( approve: ApprovePortOpeningRequestOnPrem, id: number) {
		return this.patch<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}/approval`, approve);
	}

	async createCHG(id: number) {
		return this.post<PortOpeningRequestOnPrem>(`OnPremPortOpeningRequests/${id}/createCHG`, null);
	}

	async saveDraft(por: SubmitPortOpeningRequestOnPrem) {
		return this.put<PortOpeningRequestOnPrem>('OnPremPortOpeningRequests/draft', por);
	}

	async parse(type: OnPremPortOpeningRequestsTypes, file: File) {
		const formData = new FormData();
		formData.append(file.name, file);
		return this.postFormData(`parse/onPremRules/${type.name}`, formData);
	}
}	
